import { useCallback, useState } from "react";

/**
 * Per-item text drafts (keyed by stage / item id) so typing in one item never
 * carries over to another, and clearing one draft leaves the others intact.
 */
export type Drafts = Record<string, string>;

export const getDraft = (drafts: Drafts, key: string) => drafts[key] ?? "";

export const setDraft = (drafts: Drafts, key: string, value: string): Drafts => ({
  ...drafts,
  [key]: value,
});

export const clearDraft = (drafts: Drafts, key: string): Drafts => {
  if (!(key in drafts)) return drafts;
  const next = { ...drafts };
  delete next[key];
  return next;
};

export function useDrafts() {
  const [drafts, setDrafts] = useState<Drafts>({});
  const get = useCallback((key: string) => getDraft(drafts, key), [drafts]);
  const set = useCallback(
    (key: string, value: string) => setDrafts((d) => setDraft(d, key, value)),
    [],
  );
  const clear = useCallback((key: string) => setDrafts((d) => clearDraft(d, key)), []);
  return { get, set, clear };
}
